/**
 * The trackers map: every analytics / ad / monitoring tool an app can load,
 * whether it is switched on, and which env var feeds it.
 *
 * Each app declares its map in `src/trackers.ts`. This module holds the shape
 * and the single on/off rule, used by gatsby-config (plugins), gatsby-ssr (head
 * scripts), the runtime (init + adapters) and the dev toolbar's tracking panel.
 *
 * No browser or app imports here: gatsby-config loads this in Node. Apps import
 * it by relative path, which Gatsby bundles into the compiled config; @base and
 * @neontree/gatsby-base imports are left for Node, which can't load .ts.
 */

export type EnvVar = { name: string; value: string };

/**
 * Pair an env var's name with its value. Always call as
 * `env("GATSBY_X", process.env.GATSBY_X)`: Gatsby only inlines GATSBY_* vars
 * into the browser bundle when `process.env.GATSBY_X` is written out literally.
 */
export function env(name: string, value: string | undefined): EnvVar {
  return { name, value: value ?? "" };
}

export type TrackersConfig = {
  /** Meta pixel (browser events). Purchase is sent server-side by lovelica-triggers. */
  meta: { enabled: boolean; pixelID: EnvVar };
  /** Snap pixel (browser events). Purchase is sent server-side by lovelica-triggers. */
  snap: { enabled: boolean; pixelID: EnvVar };
  /** BIGO Ads pixel (browser events). Purchase is sent server-side by lovelica-triggers. */
  bigo: { enabled: boolean; pixelID: EnvVar };
  /**
   * Google Ads tag (gtag.js). Sends the first purchase from the browser until the
   * server-side upload lands. `conversionID` is "AW-123" or just "123".
   */
  googleAds: { enabled: boolean; conversionID: EnvVar; purchaseLabel: EnvVar };
  /** GTM web container: GA4 (through the Stape server container) + the old Google Ads Purchase tag. */
  gtm: {
    enabled: boolean;
    id: EnvVar;
    /** Load gtm.js from our own domain (Stape) instead of Google. Omit to load from Google. */
    selfHostedOrigin?: EnvVar;
    ga4ServerContainerURL?: EnvVar;
  };
  /** Product analytics, session replay, error tracking, logs. */
  posthog: { enabled: boolean; key: EnvVar; host: EnvVar };
  /** Email marketing: onsite identify + events. */
  klaviyo: { enabled: boolean; key: EnvVar };
  /** Error monitoring. */
  sentry: { enabled: boolean; dsn: EnvVar };
};

export type TrackerName = keyof TrackersConfig;

/** Env vars a tracker cannot run without; the rest only tune it. Adding a tracker? Add it here too. */
const REQUIRED_ENV: { [K in TrackerName]: Array<keyof TrackersConfig[K]> } = {
  meta: ["pixelID"],
  snap: ["pixelID"],
  bigo: ["pixelID"],
  googleAds: ["conversionID", "purchaseLabel"],
  gtm: ["id"],
  posthog: ["key"],
  klaviyo: ["key"],
  sentry: ["dsn"],
};

export const TRACKER_NAMES = Object.keys(REQUIRED_ENV) as TrackerName[];

/** Ad pixels never run in `gatsby develop`, so local testing doesn't pollute ad data. */
const PRODUCTION_ONLY: TrackerName[] = ["meta", "snap", "bigo", "googleAds"];

export type TrackerStatus = {
  name: TrackerName;
  on: boolean;
  /** Why it is off, or "on". */
  reason: string;
  env: EnvVar[];
};

export function getTrackerStatus(
  trackers: TrackersConfig,
  name: TrackerName,
  isProduction: boolean = process.env.NODE_ENV === "production",
): TrackerStatus {
  const tracker = trackers[name] as { enabled: boolean } & Record<string, unknown>;
  const envVars = Object.values(tracker).filter(isEnvVar);
  const status = (on: boolean, reason: string) => ({ name, on, reason, env: envVars });

  if (!tracker.enabled) {
    return status(false, "disabled in trackers.ts");
  }
  const missing = (REQUIRED_ENV[name] as string[])
    .map((key) => tracker[key] as EnvVar)
    .find((v) => !v.value);
  if (missing) {
    return status(false, `${missing.name} not set`);
  }
  if (PRODUCTION_ONLY.includes(name) && !isProduction) {
    return status(false, "production builds only");
  }
  return status(true, "on");
}

/**
 * gatsby-config plugins for the trackers that load through a Gatsby plugin
 * (the rest are wired in code). Spread into each app's `plugins` list.
 */
export function getTrackerPlugins(trackers: TrackersConfig) {
  const isOn = (name: TrackerName) => getTrackerStatus(trackers, name).on;
  const plugins: Array<{ resolve: string; options: Record<string, unknown> }> = [];
  if (isOn("klaviyo")) {
    plugins.push({
      resolve: "gatsby-plugin-klaviyo",
      options: {
        klaviyoKey: trackers.klaviyo.key.value,
        enableDuringDevelop: true,
      },
    });
  }
  if (isOn("gtm")) {
    plugins.push({
      resolve: "gatsby-plugin-google-tagmanager",
      options: {
        id: trackers.gtm.id.value,
        includeInDevelopment: true,
        routeChangeEventName: "route_change",
        enableWebVitalsTracking: true,
        // The plugin rejects an empty selfHostedOrigin; only pass it when set.
        ...(trackers.gtm.selfHostedOrigin?.value
          ? { selfHostedOrigin: trackers.gtm.selfHostedOrigin.value }
          : {}),
      },
    });
  }
  return plugins;
}

function isEnvVar(value: unknown): value is EnvVar {
  return typeof value === "object" && value !== null && "name" in value && "value" in value;
}
